import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
  forwardRef
} from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';

import { WindowService } from 'src/shared/providers/window.service';

import { SelectModel } from '../select/select.model';

@Component({
  selector: 'tidy-header-select',
  templateUrl: 'header-select.component.html',
  styleUrls: ['./header-select.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => HeaderSelectComponent),
      multi: true
    }
  ],
  standalone: false
})
export class HeaderSelectComponent implements ControlValueAccessor, OnInit, OnChanges, OnDestroy {

  @Input() items: SelectModel[] = [];
  @Input() form: FormControl = new FormControl();
  @Input() icon: string;
  @Input() showIcons = false;
  @Input() richItems = false;
  @Input() showItemActions = false;
  @Input() editable = false;
  @Input() isEditing = false;
  @Input() editingValue = '';
  @Input() placeholder = '';
  @Input() set selectedValue(val: string) {
    if (val !== undefined && val !== null) {
      this.value = val;
    }
  }
  @Output() optionChange: EventEmitter<string> = new EventEmitter<string>();
  @Output() editItem: EventEmitter<SelectModel> = new EventEmitter<SelectModel>();
  @Output() deleteItem: EventEmitter<SelectModel> = new EventEmitter<SelectModel>();
  @Output() editingValueChange: EventEmitter<string> = new EventEmitter<string>();
  @Output() editSave: EventEmitter<string> = new EventEmitter<string>();
  @Output() editCancel: EventEmitter<void> = new EventEmitter<void>();
  @ViewChild('searchInput') searchInput: ElementRef<HTMLInputElement>;

  value: any = '';
  isOpen = false;
  onChange: any = (_value: any) => { _value; };
  onTouched: any = () => undefined;
  showSearch = false;
  searchValue = '';
  filteredItems: SelectModel[] = [];
  private clickListener: () => void;

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    private windowService: WindowService,
    private cdr: ChangeDetectorRef
  ) {
    this.clickListener = this.onDocumentClick.bind(this);
  }

  onDocumentClick(event: MouseEvent) {
    if (!this.el.nativeElement.contains(event.target)) {
      this.isOpen = false;
      this.cdr.markForCheck();
    }
  }

  ngOnInit() {
    document.addEventListener('click', this.clickListener, true);
    const isSecondaryLayer = localStorage.getItem('isPrimaryLayer') === 'false' && this.windowService.isDesktopRes;
    if (isSecondaryLayer) {
      this.renderer.addClass(this.el.nativeElement, 'secondary-layer');
    }
    if (this.form?.value && !this.value) {
      this.value = this.form.value;
    }
    this.updateSearchVisibility();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.form && this.form?.value) {
      this.value = this.form.value;
    }
    if (changes.items) {
      this.updateSearchVisibility();
    }
    if (changes.isEditing?.currentValue === true && !changes.isEditing?.previousValue) {
      setTimeout(() => {
        const input = this.el.nativeElement.querySelector('.header-select-edit-input') as HTMLInputElement;
        if (input) {
          input.focus();
          input.select();
        }
      }, 50);
    }
  }

  writeValue(value: any): void {
    this.value = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  toggleDropdown(event: Event): void {
    event.stopPropagation();
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.clearSearch();
      if (this.showSearch) {
        setTimeout(() => {
          this.searchInput?.nativeElement?.focus();
        }, 0);
      }
      setTimeout(() => {
        const selected = this.el.nativeElement.querySelector('.header-select-dropdown-item.selected');
        selected?.scrollIntoView({ block: 'nearest' });
      }, 0);
    }
  }

  selectItem(item: SelectModel, event: Event): void {
    event.stopPropagation();
    this.value = item.value;
    this.onChange(item.value);
    this.onTouched();
    this.form?.patchValue(item.value);
    this.optionChange.emit(item.value);
    this.isOpen = false;
  }

  getSelectedViewValue(): string {
    const selectedItem = this.items?.find(item => item.value === this.value);
    return selectedItem?.viewValue || this.placeholder || '';
  }

  getSelectedIcon(): string | undefined {
    const selectedItem = this.items?.find(item => item.value === this.value);
    return selectedItem?.icon;
  }

  getSelectedIconColor(): string | null {
    const selectedItem = this.items?.find(item => item.value === this.value);
    return selectedItem?.iconColor || null;
  }

  updateSearchVisibility(): void {
    this.showSearch = this.items?.length > 5;
    if (this.searchValue) {
      const searchLower = this.searchValue.toLowerCase();
      this.filteredItems = (this.items || []).filter((item) =>
        item.viewValue?.toLowerCase().includes(searchLower)
      );
    } else {
      this.filteredItems = [...(this.items || [])];
    }
  }

  onSearchInputChange(): void {
    const value = this.searchInput?.nativeElement?.value || '';
    if (value === this.searchValue) {
      return;
    }
    this.searchValue = value;
    const searchLower = value.toLowerCase();
    if (!searchLower) {
      this.filteredItems = [...this.items];
    } else {
      this.filteredItems = this.items.filter((item) =>
        item.viewValue?.toLowerCase().includes(searchLower)
      );
    }
  }

  clearSearch(): void {
    this.searchValue = '';
    this.filteredItems = [...(this.items || [])];
    if (this.searchInput?.nativeElement) {
      this.searchInput.nativeElement.value = '';
    }
  }

  onEditInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.editingValueChange.emit(input.value);
  }

  onEditKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.editSave.emit(this.editingValue);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.editCancel.emit();
    }
  }

  onEditBlur(): void {
    this.editSave.emit(this.editingValue);
  }

  onEditSaveClick(event: Event): void {
    event.stopPropagation();
    this.editSave.emit(this.editingValue);
  }

  onEditItemClick(item: SelectModel, event: Event): void {
    event.stopPropagation();
    this.isOpen = false;
    this.editItem.emit(item);
  }

  onDeleteItemClick(item: SelectModel, event: Event): void {
    event.stopPropagation();
    this.deleteItem.emit(item);
  }

  ngOnDestroy() {
    document.removeEventListener('click', this.clickListener, true);
  }
}
